// crearTabla({ columnas, filas, filaVacia }) → HTMLElement
//
// Helper de DOM plano (no Web Component): a diferencia de badge/modal/toast,
// una tabla de datos necesita que el llamador le pase renderers de celda
// arbitrarios (por ejemplo, una celda que es un <pf-badge-estado> o un
// botón de acción) — eso se expresa con más naturalidad como funciones JS
// que como atributos HTML. Encapsularla en Shadow DOM tampoco aportaría
// nada aquí (no hay estilo interno que proteger de fugas: usa las clases
// .pf-tabla* de estilos-base.css, iguales en ambos paneles).

/**
 * @typedef {object} ColumnaTabla
 * @property {string} clave - Debe existir como propiedad en cada fila,
 *   salvo que se provea `render`.
 * @property {string} titulo - Encabezado visible.
 * @property {(fila: object) => Node | string} [render] - Si se omite, se
 *   muestra `fila[clave]` como texto plano.
 */

/**
 * @param {object} config
 * @param {ColumnaTabla[]} config.columnas
 * @param {object[]} config.filas
 * @param {string} [config.filaVacia] - Mensaje cuando `filas` está vacío.
 * @returns {HTMLDivElement} el envoltorio `.pf-tabla-envoltorio` listo para
 *   insertar en el DOM.
 */
export function crearTabla({ columnas, filas, filaVacia = 'No hay datos para mostrar.' }) {
  const envoltorio = document.createElement('div');
  envoltorio.className = 'pf-tabla-envoltorio';

  if (!filas || filas.length === 0) {
    const vacio = document.createElement('div');
    vacio.className = 'pf-tabla__vacio';
    vacio.textContent = filaVacia;
    envoltorio.appendChild(vacio);
    return envoltorio;
  }

  const tabla = document.createElement('table');
  tabla.className = 'pf-tabla';

  const thead = document.createElement('thead');
  const filaEncabezado = document.createElement('tr');
  for (const columna of columnas) {
    const th = document.createElement('th');
    th.textContent = columna.titulo;
    filaEncabezado.appendChild(th);
  }
  thead.appendChild(filaEncabezado);
  tabla.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const fila of filas) {
    const tr = document.createElement('tr');
    for (const columna of columnas) {
      const td = document.createElement('td');
      const valor = columna.render ? columna.render(fila) : (fila[columna.clave] ?? '—');
      if (valor instanceof Node) {
        td.appendChild(valor);
      } else {
        td.textContent = String(valor);
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  tabla.appendChild(tbody);

  envoltorio.appendChild(tabla);
  return envoltorio;
}
